/**
 * U.S. state comprehensive consumer privacy laws in effect, and which rights
 * each grants. Rendered as the comparison table in the Privacy Policy and used
 * for the state list on the privacy request form.
 *
 * We honor every listed right for residents of every listed state regardless
 * of which boxes a particular statute ticks — the table is informational.
 */
export interface StatePrivacyLaw {
  state: string
  code: string
  law: string
  effective: string
  access: boolean
  correct: boolean
  delete: boolean
  portability: boolean
  optOutSale: boolean
  optOutTargetedAds: boolean
  optOutProfiling: boolean
  appeal: boolean
  /** Rights or procedures specific to this state. */
  note?: string
}

export const statePrivacyLaws: StatePrivacyLaw[] = [
  {
    state: 'California',
    code: 'CA',
    law: 'California Consumer Privacy Act, as amended by the CPRA (CCPA)',
    effective: 'Jan 1, 2020 (amended Jan 1, 2023)',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: false,
    appeal: false,
    note: 'Opt-out covers both "sale" and "sharing" for cross-context behavioral advertising. Right to limit use of sensitive personal information. See Section 10.',
  },
  {
    state: 'Colorado',
    code: 'CO',
    law: 'Colorado Privacy Act (CPA)',
    effective: 'Jul 1, 2023',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
    note: 'Universal opt-out mechanisms such as Global Privacy Control are honored as a valid opt-out.',
  },
  {
    state: 'Connecticut',
    code: 'CT',
    law: 'Connecticut Data Privacy Act (CTDPA)',
    effective: 'Jul 1, 2023',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
  },
  {
    state: 'Delaware',
    code: 'DE',
    law: 'Delaware Personal Data Privacy Act (DPDPA)',
    effective: 'Jan 1, 2025',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
    note: 'Right to obtain a list of the categories of third parties to which we have disclosed your personal data.',
  },
  {
    state: 'Indiana',
    code: 'IN',
    law: 'Indiana Consumer Data Protection Act (ICDPA)',
    effective: 'Jan 1, 2026',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
  },
  {
    state: 'Iowa',
    code: 'IA',
    law: 'Iowa Consumer Data Protection Act (ICDPA)',
    effective: 'Jan 1, 2025',
    access: true,
    correct: false,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: false,
    optOutProfiling: false,
    appeal: true,
  },
  {
    state: 'Kentucky',
    code: 'KY',
    law: 'Kentucky Consumer Data Protection Act (KCDPA)',
    effective: 'Jan 1, 2026',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
  },
  {
    state: 'Maryland',
    code: 'MD',
    law: 'Maryland Online Data Privacy Act (MODPA)',
    effective: 'Oct 1, 2025',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
    note: 'Right to obtain a list of the categories of third parties to which we have disclosed your personal data.',
  },
  {
    state: 'Minnesota',
    code: 'MN',
    law: 'Minnesota Consumer Data Privacy Act (MCDPA)',
    effective: 'Jul 31, 2025',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
    note: 'Right to obtain a list of the specific third parties to which we have disclosed your personal data, and to question the result of profiling, be told the reasons, and review the data used.',
  },
  {
    state: 'Montana',
    code: 'MT',
    law: 'Montana Consumer Data Privacy Act (MCDPA)',
    effective: 'Oct 1, 2024',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
  },
  {
    state: 'Nebraska',
    code: 'NE',
    law: 'Nebraska Data Privacy Act (NDPA)',
    effective: 'Jan 1, 2025',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
  },
  {
    state: 'New Hampshire',
    code: 'NH',
    law: 'New Hampshire Privacy Act (SB 255)',
    effective: 'Jan 1, 2025',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
  },
  {
    state: 'New Jersey',
    code: 'NJ',
    law: 'New Jersey Data Privacy Act (NJDPA)',
    effective: 'Jan 15, 2025',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
  },
  {
    state: 'Oregon',
    code: 'OR',
    law: 'Oregon Consumer Privacy Act (OCPA)',
    effective: 'Jul 1, 2024',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
    note: 'Right to obtain a list of the specific third parties (other than natural persons) to which we have disclosed your personal data.',
  },
  {
    state: 'Rhode Island',
    code: 'RI',
    law: 'Rhode Island Data Transparency and Privacy Protection Act',
    effective: 'Jan 1, 2026',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
  },
  {
    state: 'Tennessee',
    code: 'TN',
    law: 'Tennessee Information Protection Act (TIPA)',
    effective: 'Jul 1, 2025',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
  },
  {
    state: 'Texas',
    code: 'TX',
    law: 'Texas Data Privacy and Security Act (TDPSA)',
    effective: 'Jul 1, 2024',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
  },
  {
    state: 'Utah',
    code: 'UT',
    law: 'Utah Consumer Privacy Act (UCPA)',
    effective: 'Dec 31, 2023',
    access: true,
    correct: false,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: false,
    appeal: false,
  },
  {
    state: 'Virginia',
    code: 'VA',
    law: 'Virginia Consumer Data Protection Act (VCDPA)',
    effective: 'Jan 1, 2023',
    access: true,
    correct: true,
    delete: true,
    portability: true,
    optOutSale: true,
    optOutTargetedAds: true,
    optOutProfiling: true,
    appeal: true,
  },
]

/** All states + DC for the request form's residence field. */
export const usStates: { code: string; name: string }[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
]

/** Request types offered on the privacy request form. */
export const privacyRequestTypes: { value: string; label: string }[] = [
  {
    value: 'opt-out',
    label:
      'Opt out of the sale or sharing of my personal information, targeted advertising, and profiling',
  },
  { value: 'access', label: 'Access / know what personal information you have' },
  { value: 'portability', label: 'Get a portable copy of my personal information' },
  { value: 'correct', label: 'Correct inaccurate personal information' },
  { value: 'delete', label: 'Delete my personal information' },
  {
    value: 'third-parties',
    label: 'List the third parties my personal information was disclosed to',
  },
  { value: 'marketing', label: 'Stop marketing emails, calls, and texts' },
  { value: 'appeal', label: 'Appeal a decision on a previous request' },
]
